import { OrganizationSwitcher, UserButton } from '@clerk/clerk-react';
import {
  BadgeDollarSign,
  Building2,
  Home,
  KeyRound,
  Layers,
  ListChecks,
  Mail,
  MessageSquarePlus,
  Receipt,
  ScanSearch,
  Search,
  Settings2,
  Swords,
  Tag,
  Target,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { env } from '@/lib/env';
import { useAdminAccessRequests } from '@/hooks/useAdmin';
import { useScreenshotAuditQueue } from '@/hooks/useScreenshotAudit';
import { DEFAULT_EXCLUDED_TEAMS } from '@/lib/teams';
import { BrandMark } from './BrandMark';
import { ThemeToggle } from './ThemeToggle';
import { Shell, type NavSection } from './Shell';
import { ViewSwitcher } from './ViewSwitcher';
import { TeamScopeSwitcher } from './TeamScopeSwitcher';
import { TooltipProvider } from '@/components/ui/tooltip';

/**
 * Admin navigation, grouped by job:
 *   Home · Clipping · Influencer · Vyral · Sourcing · Money · Setup
 *
 * Influencer and Vyral are their own sections: the same pages, pinned to
 * that team (see TeamScopeLock in router.tsx). Vyral also shows in the
 * default views, with Social Media. Badges count what needs someone.
 */
export function AdminLayout(): JSX.Element {
  // Live, like Home and the Proof review default: expired, rejected and
  // hidden invites aren't waiting on anyone.
  // Matches the main Proof review page's default scope (Social Media + Vyral).
  const proofs = useScreenshotAuditQueue({
    state: 'pending',
    limit: 1,
    live: true,
    excludeTeam: DEFAULT_EXCLUDED_TEAMS.join(','),
  });
  const access = useAdminAccessRequests('PENDING');
  const proofCount = proofs.data?.pendingTotal ?? 0;
  const accessCount = access.data?.meta.total ?? access.data?.data.length ?? 0;

  const sections: NavSection[] = [
    {
      label: null,
      items: [
        { to: '/admin', label: 'Home', icon: Home, end: true },
      ],
    },
    {
      label: 'Clipping',
      items: [
        { to: '/admin/invites', label: 'Invites', icon: ListChecks },
        { to: '/admin/screenshot-audit', label: 'Proof review', icon: ScanSearch, badge: proofCount },
        { to: '/admin/outreachers', label: 'Outreachers', icon: Users, badge: accessCount },
        { to: '/admin/message-quotas', label: 'Message quotas', icon: Target },
        { to: '/admin/outreach-accounts', label: 'Outreach accounts', icon: KeyRound },
        { to: '/admin/creator-requests', label: 'Creator requests', icon: MessageSquarePlus },
        { to: '/admin/challenges', label: 'Challenges', icon: Swords },
        { to: '/admin/payroll', label: 'Payroll', icon: Wallet },
        { to: '/admin/insights', label: 'Insights', icon: TrendingUp },
      ],
    },
    {
      label: 'Influencer',
      items: [
        { to: '/admin/influencer/requests', label: 'Creator requests', icon: MessageSquarePlus },
        { to: '/admin/influencer/invites', label: 'Invites', icon: ListChecks },
        { to: '/admin/influencer/outreachers', label: 'Outreachers', icon: Users },
        { to: '/admin/influencer/message-quotas', label: 'Message quotas', icon: Target },
        { to: '/admin/influencer/payroll', label: 'Payroll', icon: Wallet },
        { to: '/admin/influencer/performance', label: 'Insights', icon: TrendingUp },
      ],
    },
    {
      label: 'Vyral',
      items: [
        { to: '/admin/vyral/invites', label: 'Invites', icon: ListChecks },
        { to: '/admin/vyral/outreachers', label: 'Outreachers', icon: Users },
        { to: '/admin/vyral/message-quotas', label: 'Message quotas', icon: Target },
        { to: '/admin/vyral/payroll', label: 'Payroll', icon: Wallet },
        { to: '/admin/vyral/performance', label: 'Insights', icon: TrendingUp },
      ],
    },
    ...(env.discoveryApiUrl
      ? [
          {
            label: 'Sourcing',
            items: [
              { to: '/admin/discovery', label: 'Discovery', icon: Search },
              { to: '/admin/email', label: 'Emails', icon: Mail },
              { to: '/admin/prospects', label: 'Lead queue', icon: Layers },
            ],
          } satisfies NavSection,
        ]
      : []),
    {
      label: 'Money',
      items: [
        { to: '/admin/payouts', label: 'Payouts', icon: Receipt },
      ],
    },
    {
      label: 'Setup',
      items: [
        { to: '/admin/setup/teams', label: 'Teams', icon: Building2 },
        { to: '/admin/setup/compensation', label: 'Compensation', icon: BadgeDollarSign },
        { to: '/admin/niches', label: 'Niches', icon: Tag },
        { to: '/admin/system', label: 'System', icon: Settings2 },
      ],
    },
  ];

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      <Shell
        brand={<BrandMark />}
        sections={sections}
        scopeLabel="Admin"
        headerSlot={
          <>
            <TeamScopeSwitcher />
            <ViewSwitcher />
            <ThemeToggle />
            <OrganizationSwitcher
              hidePersonal
              afterSelectOrganizationUrl="/admin"
              afterCreateOrganizationUrl="/admin"
            />
            <UserButton afterSignOutUrl="/sign-in" />
          </>
        }
      />
    </TooltipProvider>
  );
}
